import React, { useState, useEffect, useCallback, useRef } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Loading from '../ui/Loading';
import Alert from '../ui/Alert';
import Badge from '../ui/Badge';
import AlarmList from './AlarmList';
import { 
  Bell, 
  BellOff, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  Activity 
} from 'lucide-react';
import { alarmService } from '../../services/alarm.service';
import { AlarmPollStatus } from '../../types/alarm.types';

interface AlarmManagementTabProps {
  locationId: number;
}

/**
 * AlarmManagementTab Component
 * 
 * Manages HikVision cloud alarm polling for a location
 * Displays polling status, controls, and alarm list
 */
const AlarmManagementTab: React.FC<AlarmManagementTabProps> = ({ locationId }) => {
  const [status, setStatus] = useState<AlarmPollStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isToggling, setIsToggling] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResults, setTestResults] = useState<any>(null);

  const statusSequence = useRef(0);
  const testSequence = useRef(0);
  const statusController = useRef<AbortController | null>(null);
  const statusPending = useRef(false);

  const fetchStatus = useCallback(async (showLoading = false) => {
    if (statusPending.current && !showLoading) return;
    statusController.current?.abort();
    const controller = new AbortController();
    statusController.current = controller;
    const sequence = ++statusSequence.current;
    statusPending.current = true;
    try {
      if (showLoading) setIsLoading(true);
      const statusData = await alarmService.getAlarmPollingStatus(locationId, controller.signal);
      if (sequence === statusSequence.current) setStatus(statusData);
    } catch (err) {
      if (sequence === statusSequence.current && !controller.signal.aborted) {
        setError(err instanceof Error ? err.message : 'Failed to fetch alarm status');
      }
    } finally {
      if (sequence === statusSequence.current) { setIsLoading(false); statusPending.current = false; }
    }
  }, [locationId]);

  useEffect(() => {
    setStatus(null); setTestResults(null); setError(null); setIsTesting(false);
    void fetchStatus(true);
    const interval = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') void fetchStatus();
    }, 10000);
    return () => {
      window.clearInterval(interval);
      statusController.current?.abort();
      statusSequence.current++; testSequence.current++; statusPending.current = false;
    };
  }, [fetchStatus]);

  const handleTogglePolling = async () => {
    if (!status) return;

    try {
      setIsToggling(true);
      setError(null);
      
      if (status.alarm_polling.enabled) {
        await alarmService.disableAlarmPolling(locationId);
      } else {
        await alarmService.enableAlarmPolling(locationId);
      }
      
      // Refresh status
      await fetchStatus();
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to toggle alarm polling';
      setError(errorMsg);
    } finally {
      setIsToggling(false);
    }
  };

  const handleTestConnection = async () => {
    const sequence = ++testSequence.current;
    try {
      setIsTesting(true); setError(null); setTestResults(null);
      const results = await alarmService.testAlarmConnection(locationId);
      if (sequence !== testSequence.current) return;
      setTestResults(results);
      await fetchStatus();
    } catch (err) {
      if (sequence === testSequence.current) setError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      if (sequence === testSequence.current) setIsTesting(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    
    // Poll timestamps describe server work in UTC, including older offsetless responses.
    const date = new Date(/Z$|[+-]\d\d:\d\d$/.test(dateString) ? dateString : `${dateString}Z`);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loading size="lg" text="Loading alarm settings..." />
      </div>
    );
  }

  if (!status) {
    return (
      <Alert 
        variant="error" 
        message="Failed to load alarm settings" 
      />
    );
  }

  const isHikVision = status.cloud_provider === 'hikvision';

  if (!isHikVision) {
    return (
      <Card padding="lg">
        <div className="text-center py-12">
          <BellOff className="mx-auto text-text-muted mb-3" size={48} />
          <h3 className="text-lg font-semibold text-text-primary mb-2">
            Cloud Alarms Not Available
          </h3>
          <p className="text-text-muted">
            This location does not use HikVision cloud services.
          </p>
          <p className="text-sm text-text-muted mt-2">
            Current provider: <span className="font-mono">{status.cloud_provider || 'None'}</span>
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert 
          variant="error" 
          message={error} 
          onClose={() => setError(null)} 
        />
      )}

      {/* Status Card */}
      <Card padding="lg">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`
              p-3 rounded-lg 
              ${status.alarm_polling.is_healthy 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-100 text-gray-700'
              }
            `}>
              <Activity size={24} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                Alarm Polling Status
              </h3>
              <p className="text-sm text-text-muted">
                HikVision cloud alarm monitoring
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RefreshCw size={16} />}
              onClick={() => void fetchStatus()}
              disabled={isLoading}
            >
              Refresh
            </Button>
            
            {status.alarm_polling.has_credentials && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleTestConnection}
                disabled={isTesting}
              >
                {isTesting ? 'Testing...' : 'Test Connection'}
              </Button>
            )}

            <Button
              variant={status.alarm_polling.enabled ? 'danger' : 'primary'}
              size="sm"
              leftIcon={status.alarm_polling.enabled ? <BellOff size={16} /> : <Bell size={16} />}
              onClick={handleTogglePolling}
              disabled={isToggling || !status.alarm_polling.has_credentials}
            >
              {isToggling 
                ? 'Processing...' 
                : status.alarm_polling.enabled 
                  ? 'Disable Polling' 
                  : 'Enable Polling'
              }
            </Button>
          </div>
        </div>

        {/* Status Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Polling Status */}
          <div>
            <label className="text-sm font-medium text-text-muted mb-2 block">
              Polling Status
            </label>
            <div className="flex items-center gap-2">
              {status.alarm_polling.enabled ? (
                <Badge variant="success" className="flex items-center gap-1.5">
                  <CheckCircle size={14} />
                  Enabled
                </Badge>
              ) : (
                <Badge variant="default" className="flex items-center gap-1.5">
                  <XCircle size={14} />
                  Disabled
                </Badge>
              )}
            </div>
          </div>

          {/* Subscription Status */}
          <div>
            <label className="text-sm font-medium text-text-muted mb-2 block">
              Cloud Subscription
            </label>
            <div className="flex items-center gap-2">
              {status.alarm_polling.subscription_active ? (
                <Badge variant="success" className="flex items-center gap-1.5">
                  <CheckCircle size={14} />
                  Active
                </Badge>
              ) : (
                <Badge variant="default" className="flex items-center gap-1.5">
                  <XCircle size={14} />
                  Inactive
                </Badge>
              )}
            </div>
          </div>

          {/* Health Status */}
          <div>
            <label className="text-sm font-medium text-text-muted mb-2 block">
              Overall Health
            </label>
            <div className="flex items-center gap-2">
              {status.alarm_polling.is_healthy ? (
                <Badge variant="success" className="flex items-center gap-1.5">
                  <CheckCircle size={14} />
                  Healthy
                </Badge>
              ) : (
                <Badge variant="warning" className="flex items-center gap-1.5">
                  <AlertCircle size={14} />
                  Issues Detected
                </Badge>
              )}
            </div>
          </div>

          {/* Last Success */}
          <div>
            <label className="text-sm font-medium text-text-muted mb-2 block">
              Last Successful Poll
            </label>
            <p className="text-text-primary font-medium">
              {formatDate(status.alarm_polling.last_success)}
            </p>
            {status.alarm_polling.enabled && status.alarm_polling.last_success_recent === false && (
              <p className="mt-1 text-xs text-text-muted">Waiting for a completed alarm import.</p>
            )}
          </div>

          {/* Credentials Status */}
          <div>
            <label className="text-sm font-medium text-text-muted mb-2 block">
              Cloud Credentials
            </label>
            <div className="flex items-center gap-2">
              {status.alarm_polling.has_credentials ? (
                <Badge variant="success" className="flex items-center gap-1.5">
                  <CheckCircle size={14} />
                  Configured
                </Badge>
              ) : (
                <Badge variant="danger" className="flex items-center gap-1.5">
                  <XCircle size={14} />
                  Missing
                </Badge>
              )}
            </div>
          </div>

          {/* Error Status */}
          {status.alarm_polling.last_error && (
            <div className="col-span-3">
              <label className="text-sm font-medium text-text-muted mb-2 block">
                Last Error
              </label>
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">
                  {status.alarm_polling.last_error}
                </p>
                {status.alarm_polling.error_code && (
                  <p className="text-xs text-red-600 mt-1 font-mono">
                    Code: {status.alarm_polling.error_code}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Missing Credentials Warning */}
        {!status.alarm_polling.has_credentials && (
          <Alert 
            variant="warning" 
            message="Cloud credentials are missing. Please configure HikVision API credentials in the location settings before enabling alarm polling."
            className="mt-4"
          />
        )}
      </Card>

      {/* Test Results */}
      {testResults && (
        <Card padding="lg">
          <h3 className="text-lg font-semibold text-text-primary mb-4">
            Connection Test Results
          </h3>
          
          <div className="space-y-3">
            {testResults.tests?.map((test: any, index: number) => (
              <div 
                key={index}
                className={`
                  p-4 rounded-lg border
                  ${test.passed 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                  }
                `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {test.passed ? (
                      <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
                    ) : (
                      <XCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                    )}
                    <div>
                      <p className={`font-medium ${test.passed ? 'text-green-800' : 'text-red-800'}`}>
                        {test.name}
                      </p>
                      {test.message && (
                        <p className={`text-sm mt-1 ${test.passed ? 'text-green-700' : 'text-red-700'}`}>
                          {test.message}
                        </p>
                      )}
                      {test.error && (
                        <p className="text-sm mt-1 text-red-700">
                          {test.error}
                        </p>
                      )}
                    </div>
                  </div>
                  <Badge variant={test.passed ? 'success' : 'danger'} className="text-xs">
                    {test.passed ? 'Passed' : 'Failed'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>

          {testResults.polling_retry_scheduled && (
            <p role="status" className="mt-4 text-sm text-text-secondary">Cloud access verified. Automatic polling will retry; this status updates when alarms are imported.</p>
          )}
          <div className="mt-4 p-4 bg-surface-secondary rounded-lg">
            <div className="flex items-center justify-between">
              <span className="font-medium text-text-primary">
                Overall Status:
              </span>
              <Badge 
                variant={testResults.overall_success ? 'success' : 'danger'}
                className="text-sm"
              >
                {testResults.overall_success ? 'All Tests Passed' : 'Some Tests Failed'}
              </Badge>
            </div>
          </div>
        </Card>
      )}

      {/* Alarm List */}
      {status.alarm_polling.enabled && (
        <AlarmList locationId={locationId} />
      )}
    </div>
  );
};

export default AlarmManagementTab;
